import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { useAppSelector } from '@/redux/hooks'
import { formatCurrency } from '@/utils'
import { currencySymbol } from '@/utils/format'
import { useStoreCurrency } from '@/hooks/use-store-currency'
import {
  isOfficialMagentoRestApiEnabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import {
  OpenKey,
  SetOpenKey,
  ValuePopover,
  bindOpen
} from '@/ui/components/rule-list'
import { isValidCost } from './shipping-rules'

const { Input, Switch, FieldRoot, FieldLabel, ToggleGroup, ToggleGroupItem } =
  Base

const PRESETS = ['0', '2.95', '4.95', '7.95']

export interface Cost {
  cost: string
  returnOriginalShippingCosts: boolean
}

// Only these platforms can refund the original shipping; a rule that already does keeps the switch.
function useCanRefundOriginal() {
  const platform = useAppSelector(
    (s) => s.settingsApi.settings.response.platform
  )
  const { isFlagEnabled } = useFeatureFlag(isOfficialMagentoRestApiEnabled)
  return (
    platform === 'SHOPIFY' ||
    platform === 'PRESTASHOP' ||
    platform === 'CUSTOM_GENERIC' ||
    (platform === 'MAGENTO_V2' && isFlagEnabled)
  )
}

export const CostCell: React.FC<{
  id: string
  value: Cost
  label: string
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (value: Cost) => void
}> = ({ id, value, label, editable, openKey, setOpenKey, onChange }) => {
  const { t, i18n } = useTranslation()
  const currency = useStoreCurrency()
  const canRefundOriginal = useCanRefundOriginal()
  const valid = isValidCost(value.cost)
  const free = valid && Number(value.cost) === 0
  const done = () => setOpenKey(null)

  const triggerClassName =
    'inline-flex w-48 shrink-0 items-center justify-end gap-1.5 rounded-md px-2 py-1 text-sm font-medium tabular-nums outline-none ' +
    (editable ? 'hover:bg-neutral-100 focus-visible:bg-neutral-100 ' : '') +
    (valid ? (free ? 'text-success-main' : 'text-neutral-900') : 'text-red-500')
  const triggerContent = (
    <>
      {value.returnOriginalShippingCosts ? (
        <span className="text-xs font-normal text-neutral-400">
          {t('shipping_rules.plus_original')}
        </span>
      ) : null}
      {valid
        ? free
          ? t('shipping_rules.free')
          : formatCurrency(value.cost, currency)
        : t('shipping_rules.set_cost')}
    </>
  )

  if (!editable)
    return <span className={triggerClassName}>{triggerContent}</span>
  return (
    <ValuePopover
      {...bindOpen(`${id}:cost`, openKey, setOpenKey)}
      triggerLabel={label}
      triggerClassName={triggerClassName}
      triggerContent={triggerContent}
      className="w-72"
    >
      <div className="flex flex-col gap-3 p-3">
        <FieldRoot invalid={!valid}>
          <FieldLabel>{t('shipping_rules.customer_pays')}</FieldLabel>
          <Input
            autoFocus
            prefix={currencySymbol(currency, i18n.language)}
            inputMode="decimal"
            value={value.cost}
            onChange={(event) =>
              onChange({ ...value, cost: event.target.value.replace(',', '.') })
            }
            onKeyDown={(event) => event.key === 'Enter' && done()}
          />
          <ToggleGroup
            variant="button"
            aria-label={t('shipping_rules.customer_pays')}
            value={PRESETS.filter(
              (preset) => valid && Number(value.cost) === Number(preset)
            )}
            onValueChange={([preset]: string[]) =>
              preset && onChange({ ...value, cost: preset })
            }
          >
            {PRESETS.map((preset) => (
              <ToggleGroupItem key={preset} value={preset}>
                {preset === '0'
                  ? t('shipping_rules.free')
                  : formatCurrency(preset, currency)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </FieldRoot>
        {canRefundOriginal || value.returnOriginalShippingCosts ? (
          <div className="flex items-center justify-between gap-3 border-t border-solid border-neutral-100 pt-3">
            <label
              htmlFor={`${id}-refund-original`}
              className="text-sm text-neutral-700"
            >
              {t('shipping_rules.refund_original')}
            </label>
            <Switch
              id={`${id}-refund-original`}
              size="sm"
              checked={value.returnOriginalShippingCosts}
              onCheckedChange={(checked) =>
                onChange({ ...value, returnOriginalShippingCosts: checked })
              }
            />
          </div>
        ) : null}
      </div>
    </ValuePopover>
  )
}
